import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AOS from 'aos';
import {
  FaArrowRight, FaArrowLeft, FaCheckCircle,
  FaUserTie, FaGraduationCap, FaBuilding, FaHandshake,
  FaShoppingCart, FaBullhorn, FaCode, FaDatabase, FaCloud,
  FaMicrochip, FaLayerGroup, FaChevronRight
} from 'react-icons/fa';
import portfolioData from '../data/portfolio.json';
import './ProjectDetails.css';

const ICON_MAP = {
  FaUserTie: <FaUserTie />,
  FaGraduationCap: <FaGraduationCap />,
  FaBuilding: <FaBuilding />,
  FaHandshake: <FaHandshake />,
  FaShoppingCart: <FaShoppingCart />,
  FaBullhorn: <FaBullhorn />,
};

const TECH_LAYER_ICONS = {
  frontend: <FaCode />,
  backend: <FaLayerGroup />,
  database: <FaDatabase />,
  cloud: <FaCloud />,
  other: <FaMicrochip />,
};

const ProjectDetails = () => {
  const { id } = useParams();

  const project = portfolioData.find(p => p.id === id);

  useEffect(() => {
    AOS.init({ duration: 900, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
        <h2 style={{ color: 'var(--color-text)' }}>Project not found</h2>
        <Link to="/portfolio" className="btn-primary-vk">Back to Portfolio <FaArrowRight /></Link>
      </div>
    );
  }

  const currentIndex = portfolioData.findIndex(p => p.id === id);
  const prevProject = portfolioData[currentIndex - 1];
  const nextProject = portfolioData[currentIndex + 1];

  return (
    <div className="project-details-page">

      {/* Hero */}
      <section className="pd-hero" style={{ background: `linear-gradient(135deg, #0f172a 0%, ${project.color}33 60%, #0f172a 100%)` }}>
        <div className="pd-hero-overlay" />
        <Container>
          <div className="pd-hero-content" data-aos="fade-up">
            <div className="breadcrumb-vk">
              <Link to="/">Home</Link> /
              <Link to="/portfolio"> Portfolio</Link> /
              <span> {project.title}</span>
            </div>
            <div className="pd-hero-top">
              <div className="pd-hero-icon" style={{ background: `${project.color}25`, color: project.color }}>
                {ICON_MAP[project.icon]}
              </div>
              <div>
                <span className="pd-badge" style={{ background: project.color }}>{project.badge}</span>
                <span className="pd-status-live">● {project.status}</span>
              </div>
            </div>
            <h1 className="pd-hero-title">{project.title}</h1>
            <p className="pd-hero-tagline" style={{ color: project.color }}>{project.tagline}</p>
            <p className="pd-hero-desc">{project.description}</p>
            <div className="pd-hero-actions">
              <Link to="/contact" className="btn-primary-vk">Request Demo <FaArrowRight /></Link>
              <Link to="/contact" className="btn-outline-vk">Get a Quote</Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Highlights */}
      <section className="pd-highlights-bar">
        <Container>
          <div className="pd-highlights-grid">
            {project.highlights.map((h, i) => (
              <div key={i} className="pd-highlight-item" data-aos="fade-up" data-aos-delay={i * 80}>
                <FaCheckCircle className="pd-hl-icon" style={{ color: project.color }} />
                <span>{h}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="section-py" style={{ background: 'var(--color-bg)' }}>
        <Container>
          <Row className="g-5">

            {/* Features */}
            <Col lg={6} data-aos="fade-right">
              <span className="section-badge">Key Features</span>
              <h2 className="section-title" style={{ fontSize: '1.75rem' }}>
                What <span>It Includes</span>
              </h2>
              <div className="gradient-line" />
              <div className="pd-features-grid">
                {project.features.map((f, i) => (
                  <div key={i} className="pd-feature-item" style={{ '--pd-color': project.color }}>
                    <FaChevronRight className="pd-feat-arrow" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </Col>

            {/* Tech Stack */}
            <Col lg={6} data-aos="fade-left">
              <span className="section-badge">Technology Stack</span>
              <h2 className="section-title" style={{ fontSize: '1.75rem' }}>
                Built <span>With</span>
              </h2>
              <div className="gradient-line" />
              <div className="pd-tech-stack">
                {Object.entries(project.techStack).map(([layer, value], i) => (
                  <div key={i} className="pd-tech-layer" data-aos="fade-up" data-aos-delay={i * 60}>
                    <div className="pd-tech-layer-icon" style={{ background: `${project.color}12`, color: project.color }}>
                      {TECH_LAYER_ICONS[layer] || <FaCode />}
                    </div>
                    <div className="pd-tech-layer-body">
                      <div className="pd-tech-layer-name">
                        {layer.charAt(0).toUpperCase() + layer.replace(/([A-Z])/g, ' $1').slice(1)}
                      </div>
                      <div className="pd-tech-layer-value">
                        {Array.isArray(value) ? value.join(' · ') : value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* All Tech Pills */}
              <div className="pd-all-tech" data-aos="fade-up">
                <div className="pd-all-tech-label">Technologies Used</div>
                <div className="pd-tech-pills-wrap">
                  {project.technologies.map((t, i) => (
                    <span key={i} className="pd-tech-pill" style={{ borderColor: `${project.color}50`, color: project.color, background: `${project.color}0a` }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Use Cases */}
      <section className="pd-usecases section-py" style={{ background: 'var(--color-bg-alt)' }}>
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Who Uses It</span>
            <h2 className="section-title">Use <span>Cases</span></h2>
          </div>
          <div className="pd-usecases-grid">
            {project.useCases.map((uc, i) => (
              <div key={i} className="pd-usecase-card" style={{ '--pd-color': project.color }} data-aos="zoom-in" data-aos-delay={i * 80}>
                <FaCheckCircle className="pd-uc-icon" />
                <span>{uc}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="pd-cta-section">
        <Container>
          <div className="pd-cta-inner" data-aos="zoom-in">
            <h2 className="pd-cta-title">
              Interested in <span>{project.title}?</span>
            </h2>
            <p className="pd-cta-desc">
              Get a free demo and see how this solution can transform your business operations.
            </p>
            <div className="pd-cta-actions">
              <Link to="/contact" className="btn-primary-vk">Request Free Demo <FaArrowRight /></Link>
              <Link to="/contact" className="btn-outline-vk-white">Talk to an Expert</Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Project Navigation */}
      <section className="pd-nav-section section-py">
        <Container>
          <div className="pd-nav-row">
            {prevProject ? (
              <Link to={`/portfolio/${prevProject.id}`} className="pd-nav-card pd-nav-prev">
                <FaArrowLeft />
                <div>
                  <div className="pd-nav-label">Previous</div>
                  <div className="pd-nav-title">{prevProject.title}</div>
                </div>
              </Link>
            ) : <div />}

            <Link to="/portfolio" className="pd-back-all">
              View All Projects
            </Link>

            {nextProject ? (
              <Link to={`/portfolio/${nextProject.id}`} className="pd-nav-card pd-nav-next">
                <div style={{ textAlign: 'right' }}>
                  <div className="pd-nav-label">Next</div>
                  <div className="pd-nav-title">{nextProject.title}</div>
                </div>
                <FaArrowRight />
              </Link>
            ) : <div />}
          </div>
        </Container>
      </section>

    </div>
  );
};

export default ProjectDetails;
